import { TextBody } from '@/design-system/components/typography';
import { UserEarnOfferListResponseDataItem } from '@utx/utx-client';
import { Pressable, styled, SxProp, View } from 'dripsy';
import { useTranslation } from 'react-i18next';
import { getEarnCategoryTranslationKey } from '../../utils/getEarnCategoryTranslationKey';
import { getEarnTenorTranslationKey } from '../../utils/getEarnTenorTranslationKey';
import { AmountSummary } from '../cards/AmountSummary';
import { CurrenciesIcon, CurrencyIconVariant } from '../CurrenciesIcon';
import { PopularOffersVariant } from './PopularOffers';

const PressableView = styled(Pressable)();

export interface PopularOfferItemProps {
  offer: UserEarnOfferListResponseDataItem;
  onPress?: () => void;
  sx?: SxProp;
  variant: PopularOffersVariant;
}

export const PopularOfferItem: React.FC<PopularOfferItemProps> = ({ offer, sx, onPress, variant }) => {
  const { t } = useTranslation('earn');
  const isWeb = variant === PopularOffersVariant.Web;
  const baseStyles: SxProp = isWeb ? styles.containerWeb : styles.containerNative;
  const tenor = getEarnTenorTranslationKey(offer.tenor);

  return (
    <PressableView sx={{ ...baseStyles, ...sx }} onPress={onPress}>
      <View sx={styles.containerTop}>
        <CurrenciesIcon
          asset={offer.asset}
          subText={offer.provider}
          textSize={isWeb ? 'H6' : 'textBody'}
          variant={CurrencyIconVariant.native}
        />
        <TextBody secondary={isWeb}>{t(getEarnCategoryTranslationKey(offer.category))}</TextBody>
      </View>
      <View sx={{ ...styles.containerBottom, pt: isWeb ? 0 : '$3' }}>
        <AmountSummary label={t('earn-invest::glossary.est-apy')} value={offer.estimatedApy} asset="%" success />
        <AmountSummary label={t('earn-invest::glossary.tenor')} value={t(tenor.key, tenor.options)} alignRight />
      </View>
    </PressableView>
  );
};

enum StyleName {
  containerWeb = 'containerWeb',
  containerNative = 'containerNative',
  containerTop = 'containerTop',
  containerBottom = 'containerBottom'
}

const styles: Record<StyleName, SxProp> = {
  containerWeb: {
    width: 333,
    padding: '$5',
    mr: '$7',
    borderRadius: 10,
    shadowColor: '#000000',
    shadowOffset: {
      width: 0,
      height: 2
    },
    shadowOpacity: 0.1,
    shadowRadius: 5,
    elevation: 1,
    justifyContent: 'space-between'
  },
  containerNative: {
    flex: 1,
    maxHeight: 155,
    padding: '$3',
    borderRadius: 5,
    borderWidth: 0,
    borderColor: '$borderDivider',
    justifyContent: 'space-between',
    shadowColor: '#000000',
    shadowOffset: {
      width: 0,
      height: 2
    },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 5
  },
  containerTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start'
  },
  containerBottom: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end'
  }
};
